import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, ShoppingCart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { cartCount } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Store', path: '/store' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <motion.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/80 backdrop-blur-xl shadow-lg shadow-cyan-500/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex flex-col items-center group" data-testid="navbar-logo">
            <div className="flex items-center space-x-3">
              <div className="relative w-12 h-12 md:w-14 md:h-14">
                <img 
                  src="/logo.png" 
                  alt="UAV Geo Solutions" 
                  className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 blur-xl bg-primary/30 group-hover:bg-primary/50 transition-all -z-10"></div>
              </div>
              <div className="flex flex-col">
                <div className="text-xl md:text-2xl font-heading font-bold text-white uppercase tracking-wider leading-tight">
                  UAV GEO
                </div>
                <div className="text-[10px] md:text-xs text-white uppercase tracking-[0.2em] leading-none mt-0.5 text-center">
                  Solutions
                </div>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`px-4 py-2 text-sm font-medium uppercase tracking-wider transition-all rounded-lg relative group ${
                  location.pathname === link.path
                    ? 'text-primary'
                    : 'text-gray-300 hover:text-primary'
                }`}
              >
                {link.name}
                {location.pathname === link.path && (
                  <motion.div
                    layoutId="navbar-indicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary to-indigo-500"
                  />
                )}
              </Link>
            ))}
            {user?.role === 'admin' && (
              <Link
                to="/admin"
                data-testid="nav-link-admin"
                className="px-4 py-2 text-sm font-medium uppercase tracking-wider text-secondary hover:text-secondary-hover transition-colors rounded-lg"
              >
                Admin
              </Link>
            )}
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center space-x-3">
            {/* Cart Icon */}
            <Link to="/cart" className="relative p-2 text-gray-300 hover:text-primary transition-colors" data-testid="cart-icon">
              <ShoppingCart size={22} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-dark text-xs font-bold rounded-full flex items-center justify-center">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </Link>
            
            {user ? (
              <>
                <span className="text-sm text-gray-300 px-3">{user.full_name}</span>
                <Button
                  onClick={logout}
                  variant="outline"
                  size="sm"
                  data-testid="logout-button"
                  className="bg-transparent border-primary/50 text-primary hover:bg-primary/10 hover:border-primary uppercase tracking-wider text-xs font-semibold"
                >
                  Logout
                </Button>
              </>
            ) : (
              <Link to="/login">
                <Button
                  size="sm"
                  data-testid="login-button"
                  className="bg-transparent border border-primary/50 text-primary hover:bg-primary/10 hover:border-primary uppercase tracking-wider text-xs font-semibold"
                >
                  Login
                </Button>
              </Link>
            )}
            <Link to="/contact">
              <Button
                size="sm"
                data-testid="get-proposal-button"
                className="bg-gradient-to-r from-primary to-indigo-500 text-black hover:from-primary-hover hover:to-indigo-600 uppercase tracking-wider text-xs font-bold shadow-lg shadow-primary/30"
              >
                Get Proposal
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-2">
            {/* Mobile Cart Icon */}
            <Link to="/cart" className="relative p-2 text-gray-300 hover:text-primary transition-colors" data-testid="mobile-cart-icon">
              <ShoppingCart size={22} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-dark text-xs font-bold rounded-full flex items-center justify-center">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </Link>
            <button
              className="text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
        >
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                className={`text-sm font-medium uppercase tracking-wider py-2 ${
                  location.pathname === link.path ? 'text-primary' : 'text-gray-300'
                }`}
              >
                {link.name}
              </Link>
            ))}
            {user?.role === 'admin' && (
              <Link
                to="/admin"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-medium uppercase tracking-wider text-secondary py-2"
              >
                Admin
              </Link>
            )}
            <div className="pt-4 border-t border-white/10 space-y-3">
              {user ? (
                <Button onClick={logout} variant="outline" className="w-full">
                  Logout
                </Button>
              ) : (
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-transparent border border-primary text-primary">Login</Button>
                </Link>
              )}
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-gradient-to-r from-primary to-indigo-500 text-black">Get Proposal</Button>
              </Link>
            </div>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
};

export default Navbar;
